import React, { useState } from "react";

import { WORDS } from "../../lib/tokiPonaData";
import {
  DISTRICTS,
  GROWTH_STAGES,
  districtOfWord,
  growthStage,
  linkError,
  linksOfWord,
} from "../../lib/tokiPonaGarden";
import {
  SECTIONS,
  answeredPrompts,
  formatPrompt,
  nextPrompt,
} from "../../lib/tokiPonaPrompts";
import { Html, SpeakButton } from "../tokiPonaUi";
import HatBuilder from "./HatBuilder";
import PlantMark from "./PlantMark";

/* ============================================================
   kon nimi — a single word's journal page. Five sections of
   reflective prompts, answered one at a time and kept forever.
   ============================================================ */

const districtById = (id) => DISTRICTS.find((d) => d.id === id) || null;

// len prompts don't take free text — each asks for a sentence that puts
// the word in a specific hat.
const LEN_HATS = { "len-1": "noun", "len-2": "verb", "len-3": "modifier" };

// Render a saved len sentence with the target word bolded.
const renderTp = (text, word) =>
  text.split(" ").map((tok, i) => (
    // eslint-disable-next-line react/no-array-index-key
    <React.Fragment key={i}>
      {i > 0 ? " " : ""}
      {tok === word ? <b>{tok}</b> : tok}
    </React.Fragment>
  ));

const WordPage = ({ word, wordEntries, learned, onSave, onBack, links, onAddLink }) => {
  // { promptId, draft } for the one open editor at a time. len prompts
  // don't use draft (the chip builder keeps its own state) — draft stays
  // null and the section id tells us which UI to show.
  const [editor, setEditor] = useState(null);
  const [linkTo, setLinkTo] = useState("");
  const [linkNote, setLinkNote] = useState("");
  const [linkErr, setLinkErr] = useState(null);

  const stage = growthStage(wordEntries);
  const dictEntry = WORDS[word];
  const district = districtById(districtOfWord(word));

  const startEdit = (promptId, currentText) => {
    setEditor({ promptId, draft: promptId.startsWith("len-") ? null : currentText });
  };

  const save = () => {
    if (!editor || !editor.draft || !editor.draft.trim()) return;
    onSave(editor.promptId, editor.draft);
    setEditor(null);
  };

  const saveLen = (promptId, text) => {
    onSave(promptId, text);
    setEditor(null);
  };

  const submitLink = () => {
    const err = linkError(links, word, linkTo);
    if (err) {
      setLinkErr(err);
      return;
    }
    onAddLink(linkTo, linkNote);
    setLinkTo("");
    setLinkNote("");
    setLinkErr(null);
  };

  return (
    <div className="kn-word">
      <div className="kn-word-head">
        <button type="button" className="kn-word-back" onClick={onBack}>
          ‹ the garden
        </button>
        <div className="kn-word-title">
          <h1>{word}</h1>
          <SpeakButton text={word} />
        </div>
        {district && (
          <p className="kn-word-locator">
            lives in {district.name} · {district.gloss}
          </p>
        )}
        <div className="kn-word-growth">
          <PlantMark stage={stage} />
          <div>
            <span className="kn-word-stage">{GROWTH_STAGES[stage]}</span>
            <span className="kn-word-hint">
              answer a question in a new section and it grows
            </span>
          </div>
        </div>
      </div>

      {dictEntry && (
        <div className="kn-dict">
          <p className="kn-dict-gloss">{dictEntry.g}</p>
          <Html html={dictEntry.n} className="kn-dict-note" />
          {!learned && (
            <p className="kn-dict-unmet">
              you haven&apos;t met this word in lessons yet — journaling ahead is
              allowed.
            </p>
          )}
        </div>
      )}

      {SECTIONS.map((section) => {
        const isLen = section.id === "len";
        const answered = answeredPrompts(word, section.id, wordEntries);
        const open = nextPrompt(word, section.id, wordEntries);
        const openDraft = open && editor && editor.promptId === open.id ? editor.draft : "";
        return (
          <div className="kn-section" key={section.id}>
            <div className="kn-section-head">
              <span className="kn-section-name">{section.name}</span>
              <span className="kn-section-label">{section.label}</span>
            </div>
            <p className="kn-section-def">{section.def}</p>

            {answered.map((prompt) => {
              const entry = wordEntries[prompt.id];
              const isEditing = editor && editor.promptId === prompt.id;
              return (
                <div className="kn-entry" key={prompt.id}>
                  <p className="kn-entry-prompt">{formatPrompt(prompt.text, word)}</p>
                  {isLen ? (
                    isEditing ? (
                      <HatBuilder
                        key={prompt.id}
                        word={word}
                        hat={LEN_HATS[prompt.id]}
                        initialTokens={entry.text.split(" ")}
                        onSave={(text) => saveLen(prompt.id, text)}
                      />
                    ) : (
                      <>
                        <p className="kn-entry-tp">{renderTp(entry.text, word)}</p>
                        <button
                          type="button"
                          className="kn-edit"
                          onClick={() => startEdit(prompt.id, entry.text)}
                        >
                          edit
                        </button>
                      </>
                    )
                  ) : isEditing ? (
                    <>
                      <textarea
                        className="kn-textarea"
                        value={editor.draft}
                        placeholder="write here — nobody is grading this"
                        onChange={(e) => setEditor({ ...editor, draft: e.target.value })}
                      />
                      <button
                        type="button"
                        className="kn-save"
                        disabled={!editor.draft.trim()}
                        onClick={save}
                      >
                        save
                      </button>
                    </>
                  ) : (
                    <>
                      <p className="kn-entry-text">{entry.text}</p>
                      <button
                        type="button"
                        className="kn-edit"
                        onClick={() => startEdit(prompt.id, entry.text)}
                      >
                        edit
                      </button>
                    </>
                  )}
                </div>
              );
            })}

            {open && (
              <div className="kn-ask">
                <p className="kn-ask-prompt">
                  {formatPrompt(open.text, word)}
                  {isLen && (
                    <span className="kn-ask-hat"> — asked hat: {LEN_HATS[open.id]}</span>
                  )}
                </p>
                {isLen ? (
                  // keyed per prompt so a fresh ask starts with an empty
                  // build row instead of inheriting the previous one's tiles
                  <HatBuilder
                    key={open.id}
                    word={word}
                    hat={LEN_HATS[open.id]}
                    onSave={(text) => saveLen(open.id, text)}
                  />
                ) : (
                  <>
                    <textarea
                      className="kn-textarea"
                      value={openDraft}
                      placeholder="write here — nobody is grading this"
                      onChange={(e) => setEditor({ promptId: open.id, draft: e.target.value })}
                    />
                    <button
                      type="button"
                      className="kn-save"
                      disabled={!openDraft.trim()}
                      onClick={save}
                    >
                      save
                    </button>
                  </>
                )}
              </div>
            )}

            {!open && (
              <p className="kn-section-done">
                this section is fully grown — new questions arrive in a later
                season.
              </p>
            )}

            {section.id === "poka" && (
              <div className="kn-links">
                <p className="kn-links-head">linja · the vines</p>
                {linksOfWord(links, word).map((l) => {
                  const other = l.from === word ? l.to : l.from;
                  return (
                    <p className="kn-link-row" key={`${l.from}·${l.to}·${l.at}`}>
                      <span className="kn-link-word">{other}</span>
                      {l.note && <span className="kn-link-note"> — {l.note}</span>}
                    </p>
                  );
                })}
                <div className="kn-link-form">
                  <input
                    className="kn-link-input"
                    list="kn-all-words"
                    value={linkTo}
                    placeholder="another word…"
                    onChange={(e) => {
                      setLinkTo(e.target.value);
                      setLinkErr(null);
                    }}
                  />
                  <input
                    className="kn-link-input"
                    value={linkNote}
                    maxLength={120}
                    placeholder="how are they tied?"
                    onChange={(e) => setLinkNote(e.target.value)}
                  />
                  <button type="button" className="kn-save" onClick={submitLink}>
                    tie the vine
                  </button>
                  {linkErr && <p className="kn-link-error">{linkErr}</p>}
                </div>
              </div>
            )}
          </div>
        );
      })}

      <datalist id="kn-all-words">
        {Object.keys(WORDS).map((w) => (
          <option value={w} key={w} />
        ))}
      </datalist>
    </div>
  );
};

export default WordPage;
